import React, { useCallback, useState } from 'react';
import { NextPageContext } from 'next';
import cc from 'classcat';

import {
    Amenities,
    Breadcrumbs,
    ConsultantCard,
    EmailButton,
    CtaButtons,
    FAQs,
    Headings,
    LocationsInSection, PhoneButton,
    PhotoModal,
    RetirementVillageActivities,
    RetirementVillageServicesGlobals,
    RetirementVillageTestimonials,
    TourBookingCta,
    TourTimes,
    Units,
} from '../../../components';
import { useSiteQuery } from '../../../hooks';
import { getLocationTitle } from '../../../lib/FunctionLib';
import ErrorPage from '../../_error';
import NotFoundPage from '../../404';

import query from './index.graphql';

import styles from './index.module.scss';

interface Props {
  slug: string;
}

const RetirementVillagesLocationsPage = ({ slug }: Props) => {
  const [{ data, error }] = useSiteQuery<VillageLocationsDetailQuery>({ query, variables: { slug } });
  const [photoModalOpen, openPhotoModal] = useState(false);

  const togglePhotoModal = useCallback(() => {
    const toSet = !photoModalOpen;
    openPhotoModal(toSet);
  }, [photoModalOpen]);

  function onGalleryClick(e: React.MouseEvent<Element> | React.KeyboardEvent<Element>) {
    if (e.type !== 'keypress' || (e as React.KeyboardEvent).key === 'Enter') {
      togglePhotoModal();
    }
  }

  if (error) {
    return <ErrorPage statusCode={500} err={error} />;
  }

  const entry = data?.entry;
  if (entry) {
    const title = getLocationTitle(entry);
    const photos = entry.photos?.filter((p): p is AssetFragment => p !== null && p !== undefined) || [];
    const consultantData = Array.isArray(entry.consultant) ? entry.consultant[0] : entry.consultant;
    const consultantPhone = consultantData?.phone;

    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Retirement Villages', uri: '/retirement-villages' },
      { title, uri: `/${entry.uri!}` },
    ];
    const activities = entry.activities || [];
    const hideCtas = entry.hideContactCtas;

    return (
      <>
        {/* Hide global StickyMobile CTAs when hideContactCtas is on */}
        {hideCtas && (
          <style dangerouslySetInnerHTML={{ __html: '[class*="stickyMobile"] { display: none !important; }' }} />
        )}
        <Breadcrumbs breadcrumbs={breadcrumbs} additionalClasses={'override-bg-blue'} />

        {entry.bannerMessage && (
          <div className={cc(['sectionWrapper', styles.banner])}>
            <div className="container">
              <p>{entry.bannerMessage}</p>
            </div>
          </div>
        )}

        <Headings
          title={title}
          headline={entry.headline!}
          photos={photos}
          onGalleryClick={onGalleryClick}
          location={entry.id!}
          activities={activities}
          additionalClasses={'override-bg-blue-signature'}
          consultant={entry.consultant}
          hideContactCtas={hideCtas}
        />


        <LocationsInSection customTitle="Explore our other villages" current={entry} all={data?.allLocations} />

        <div className="section pt-4">
          <div className="container">
            <div className={cc([styles.contentRow, 'row', entry.consultant && styles.withConsultant])}>
              <div className={styles.contentColumn}>
                <div id="village-overview" className="sectionHeading">
                  Village Overview
                </div>
                <div dangerouslySetInnerHTML={{ __html: entry.body! }} />

                {entry.tourTimes &&
                    <TourTimes time={entry.tourTimes} className={styles.tourTimes} />
                }
              </div>

                {!hideCtas && (
                  <div className={styles.contactColumn}>
                      {consultantData ? (
                          <ConsultantCard consultant={entry.consultant} locationTitle={title} />
                      ) : (
                          <div className={styles.fallbackContactButtons}>
                              <CtaButtons
                                  headline="Get in touch for more details about this property"
                                  description="Send an email or give us a call during business hours"
                              >
                                  <div>
                                      <EmailButton section="Retirement Village Location" name={entry.title!} />
                                  </div>
                                  <div>
                                      <PhoneButton customPhone={entry.phone!} />
                                  </div>
                              </CtaButtons>
                          </div>
                      )}
                  </div>
                )}
            </div>
          </div>
        </div>

        <Units location={entry.id!} />

          {entry.testimonialCategory && <RetirementVillageTestimonials testimonialCategory={entry.testimonialCategory} />}
        <RetirementVillageActivities activities={entry.activities} />
        <RetirementVillageServicesGlobals
          consultant={entry.consultant}
          locationTitle={title}
          backgroundImage={entry.backgroundImage?.[0]?.url || null}
          hideContactCtas={hideCtas}
        />
        <Amenities
          title={entry.title!}
          address={entry.address!}
          amenities={entry.amenities}
          headingTextPrimary={entry.headingTextPrimary}
          headingTextSecondary={entry.headingTextSecondary}
        />

          {!hideCtas && entry.consultant && consultantPhone && (
              <TourBookingCta
                  title={`Book a tour of ${title}`}
                  subheading="Interested to view the village in person? Book a tour today!"
                  phone={consultantPhone}
                  consultantName={consultantData?.title || undefined}
                  backgroundImage={photos[0]?.url || undefined}
              />
          )}
        <FAQs topic="retirement-villages" />
        {photoModalOpen && photos && <PhotoModal photos={photos} handleClose={togglePhotoModal} />}
      </>
    );
  }
  return <NotFoundPage />;
};

RetirementVillagesLocationsPage.getInitialProps = ({ req }: NextPageContext) => ({
  slug: req?.url?.substring(req?.url?.lastIndexOf('/') + 1).replace(/\?.*$/, ''),
});

export default RetirementVillagesLocationsPage;
